import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SystemService } from '../../services/system.service';
import { SignalRService } from '../../services/signalr.service';
import { Subscription } from 'rxjs';

interface ChatMessage {
  id: string;
  text: string;
  senderName: string;
  sentOn: Date;
  hasRead: boolean;
  isCurrentUser: boolean;
}

@Component({
  selector: 'app-application-chat',
  templateUrl: './application-chat.component.html',
  styleUrl: './application-chat.component.css',
  standalone: false
})
export class ApplicationChatComponent implements OnInit, OnDestroy {
  applicationId: string = '';
  currentMessage: string = '';
  messages: ChatMessage[] = [];
  isConnected: boolean = false;
  isListening: boolean = false;
  isLoading: boolean = false;
  error: string = '';
  otherUserTyping: boolean = false;
  typingUserName: string = '';

  private typingSubscription?: Subscription;
  private newMessageSubscription?: Subscription;
  private typingTimeout: any;

  constructor(
    private systemService: SystemService,
    private signalRService: SignalRService
  ) {}

  async ngOnInit() {
    // Auto-connect to SignalR
    await this.connectToSignalR();
  }

  ngOnDestroy() {
    if (this.isListening) {
      this.stopListening();
    }
    this.typingSubscription?.unsubscribe();
    this.newMessageSubscription?.unsubscribe();
    this.signalRService.stopConnection();
  }

  async connectToSignalR() {
    try {
      // const accessToken = sessionStorage.getItem('access_token') || '';
      // await this.signalRService.startConnection('MessagingHub', () => accessToken);
      this.error = '';
    } catch (err: any) {
      this.error = 'Failed to connect to SignalR: ' + err.message;
      console.error('SignalR connection error:', err);
    }
  }

  async startListening() {
    if (!this.applicationId) {
      this.error = 'Please enter an application ID and ensure SignalR is connected';
      return;
    }

    try {
      // Join the application group and register handlers
      await this.systemService.listenForMessages(this.applicationId);
      this.isConnected = true;
      this.isListening = true;

      // Subscribe to typing notifications
      this.typingSubscription = this.systemService.typingReceived$.subscribe((data: any) => {
        console.log('Component received typing notification:', data);
        if (data.applicationId === this.applicationId) {
          console.log('Typing notification matches current applicationId, showing indicator');
          this.showTypingIndicator(data);
        }
      });

      // Subscribe to new message notifications
      this.newMessageSubscription = this.systemService.newMessageReceived$.subscribe((data: any) => {
        console.log('Component received new message notification:', data);
        if (data.applicationId === this.applicationId) {
          console.log('New message notification matches current applicationId, reloading messages');
          // Reload messages when a new message notification arrives
          this.loadMessages();
        }
      });

      // Load existing messages
      await this.loadMessages();
      this.error = '';
    } catch (err: any) {
      this.error = 'Failed to start listening: ' + err.message;
      console.error('Listen error:', err);
    }
  }

  async stopListening() {
    if (!this.applicationId) return;

    try {
      await this.systemService.stopListeningForMessages(this.applicationId);
      this.isListening = false;
      this.typingSubscription?.unsubscribe();
      this.newMessageSubscription?.unsubscribe();
      this.messages = [];
      this.error = '';
    } catch (err: any) {
      this.error = 'Failed to stop listening: ' + err.message;
      console.error('Stop listening error:', err);
    }
  }

  async loadMessages() {
    if (!this.applicationId) return;

    this.isLoading = true;
    try {
      const response = await this.systemService.getWorkflowApplicationMessages(this.applicationId).toPromise();

      if (response && response.messages) {
        this.messages = response.messages.map(msg => ({
          id: msg.id,
          text: msg.message,
          senderName: msg.sentBy?.name || 'Unknown',
          sentOn: new Date(msg.sentOn),
          hasRead: msg.hasBeenRead,
          isCurrentUser: false // TODO: Compare with current user
        }));
      }

      this.error = '';
    } catch (err: any) {
      this.error = 'Failed to load messages: ' + err.message;
      console.error('Load messages error:', err);
    } finally {
      this.isLoading = false;
    }
  }

  async sendMessage() {
    if (!this.currentMessage.trim() || !this.applicationId) return;

    try {
      const response = await this.systemService.sendApplicationMessage(
        this.applicationId,
        this.currentMessage
      ).toPromise();

      // Clear the input
      this.currentMessage = '';

      // Reload messages to show the new one
      await this.loadMessages();

      this.error = '';
    } catch (err: any) {
      this.error = 'Failed to send message: ' + err.message;
      console.error('Send message error:', err);
    }
  }

  onTyping() {
    if (!this.isListening || !this.applicationId) return;

    // Send typing notification
    this.systemService.typingMessage(this.applicationId);
  }

  async markAsRead(messageId: string) {
    try {
      await this.systemService.markAsRead(messageId).toPromise();

      // Update the message in the local array
      const message = this.messages.find(m => m.id === messageId);
      if (message) {
        message.hasRead = true;
      }

      this.error = '';
    } catch (err: any) {
      this.error = 'Failed to mark message as read: ' + err.message;
      console.error('Mark as read error:', err);
    }
  }

  private showTypingIndicator(data: any) {
    this.otherUserTyping = true;
    this.typingUserName = data.userName || 'Someone';
    console.log('Typing notification from user:', data.userName);

    // Clear existing timeout
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }

    // Hide after 3 seconds
    this.typingTimeout = setTimeout(() => {
      this.otherUserTyping = false;
    }, 3000);
  }
}
